/**
 * Anvil interaction-motion tokens + a pure transition-string builder.
 *
 * Deliberately Anvil-local (not added to the shared @appsmith/wds-theming
 * engine) so motion polish stays isolated to Anvil editor surfaces and cannot
 * affect non-Anvil widgets. Durations are in milliseconds.
 */
export const ANVIL_MOTION = {
  duration: { fast: 100, base: 160, slow: 240 },
  easing: { standard: "cubic-bezier(0.4, 0, 0.2, 1)" },
} as const;

interface TransitionOptions {
  durationMs?: number;
  easing?: string;
}

/**
 * Builds a CSS `transition` value for one or more properties using the Anvil
 * motion tokens (overridable per call).
 */
export function transition(
  properties: string | string[],
  options: TransitionOptions = {},
): string {
  const list = Array.isArray(properties) ? properties : [properties];
  const duration = options.durationMs ?? ANVIL_MOTION.duration.base;
  const easing = options.easing ?? ANVIL_MOTION.easing.standard;

  return list
    .map((property) => `${property} ${duration}ms ${easing}`)
    .join(", ");
}

/**
 * True when the user has requested reduced motion. Anvil motion (e.g. the
 * command-palette open animation) should be skipped when this is true.
 * Safe in non-browser/jsdom environments without matchMedia.
 */
export function prefersReducedMotion(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
